import { useCallback, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { FiCheck, FiChevronDown, FiLogOut, FiMapPin, FiMenu, FiSearch, FiSettings, FiUser } from "react-icons/fi";
import { logout } from "../../store/auth/index.js";
import { useGetCurrentStaffProfileQuery, useSwitchBranchMutation } from "../../store/admin/staff";
import { NotificationDropdown } from "../Notifications";
import { flattenMenu, locatePage } from "./menuModel";
import { openMenuSearch } from "./shellEvents";
import { useDismiss, useMediaQuery } from "./hooks";
import "./shell.css";

const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("") || "U";

const roleLabel = (role) => (typeof role === "string" ? role : role?.name || "");

const asBranches = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return typeof value === "object" ? [value] : [];
};

function Avatar({ photo, name }) {
  const [broken, setBroken] = useState(false);
  const usable = photo && !broken && !String(photo).endsWith("avatar.svg");
  return (
    <span className="sx-avatar">
      {usable ? <img src={photo} alt="" onError={() => setBroken(true)} /> : <span aria-hidden="true">{initialsOf(name)}</span>}
    </span>
  );
}

/** A real switcher: it lists the staff member's branches and uses the same call as the profile page. */
function BranchSwitcher({ branches, current }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const ref = useRef(null);
  const [switchBranch, { isLoading }] = useSwitchBranchMutation();
  const close = useCallback(() => {
    setOpen(false);
    setPending(null);
  }, []);
  useDismiss(ref, open, close);

  const canSwitch = branches.length > 1;

  const confirm = async () => {
    if (!pending) return;
    const toastId = toast.loading("Switching branch...");
    try {
      await switchBranch(pending.id).unwrap();
      toast.success(`Now working in ${pending.name}`, { id: toastId });
      close();
    } catch (error) {
      toast.error(error?.data?.message || "Couldn't switch branch. Try again.", { id: toastId });
    }
  };

  if (!canSwitch) {
    return (
      <span className="sx-trigger branch" style={{ cursor: "default" }} title={`Branch: ${current.name}`}>
        <FiMapPin size={16} aria-hidden="true" />
        <span className="txt">
          <b>{current.name}</b>
          <small>Active branch</small>
        </span>
        <span className="sx-sr">Active branch: {current.name}</span>
      </span>
    );
  }

  return (
    <div className="sx-pop" ref={ref}>
      <button type="button" className="sx-trigger branch" aria-haspopup="menu" aria-expanded={open} aria-label={`Branch: ${current.name}. Switch branch`} onClick={() => setOpen((v) => !v)}>
        <FiMapPin size={16} aria-hidden="true" />
        <span className="txt">
          <b>{current.name}</b>
          <small>Switch branch</small>
        </span>
        <FiChevronDown size={16} aria-hidden="true" />
      </button>
      {open ? (
        <div className="sx-menu" role="menu">
          {pending ? (
            <div className="sx-confirm">
              <p>
                Switch to <strong>{pending.name}</strong>? Lists and totals will show that branch.
              </p>
              <div className="row">
                <button type="button" onClick={() => setPending(null)}>Cancel</button>
                <button type="button" className="go" onClick={confirm} disabled={isLoading}>Switch</button>
              </div>
            </div>
          ) : (
            <>
              <p className="lab">Your branches</p>
              {branches.map((branch) => {
                const isCurrent = branch.id === current.id;
                return (
                  <button key={branch.id} type="button" role="menuitem" className={`sx-mi ${isCurrent ? "cur" : ""}`} onClick={() => (isCurrent ? close() : setPending(branch))}>
                    <FiMapPin size={16} aria-hidden="true" />
                    <span style={{ flex: 1, minWidth: 0 }}>{branch.name}</span>
                    {isCurrent ? <FiCheck size={16} aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function UserMenu({ user, guard, profilePath, settingsPath }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [confirmOut, setConfirmOut] = useState(false);
  const ref = useRef(null);
  const close = useCallback(() => {
    setOpen(false);
    setConfirmOut(false);
  }, []);
  useDismiss(ref, open, close);

  const name = user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "User";
  const subtitle =
    guard === "member"
      ? user?.member_no
        ? `No. ${user.member_no}`
        : "Member"
      : roleLabel(user?.role) || "Staff";

  return (
    <div className="sx-pop" ref={ref} data-tour="tour-profile">
      <button type="button" className="sx-trigger" aria-haspopup="menu" aria-expanded={open} aria-label={`Account menu for ${name}`} onClick={() => setOpen((v) => !v)}>
        <Avatar photo={user?.photo || user?.profile_image || user?.avatar} name={name} />
        <span className="txt">
          <b>{name}</b>
          <small>{subtitle}</small>
        </span>
        <FiChevronDown size={16} aria-hidden="true" />
      </button>
      {open ? (
        <div className="sx-menu" role="menu">
          <div className="who">
            <b>{name}</b>
            <small>{subtitle}</small>
          </div>
          {confirmOut ? (
            <div className="sx-confirm">
              <p>Log out of your account?</p>
              <div className="row">
                <button type="button" onClick={() => setConfirmOut(false)}>Stay</button>
                <button type="button" className="go bad" onClick={() => dispatch(logout())}>Log out</button>
              </div>
            </div>
          ) : (
            <>
              <Link to={profilePath} role="menuitem" className="sx-mi" onClick={close}>
                <FiUser size={16} aria-hidden="true" /> My profile
              </Link>
              {settingsPath ? (
                <Link to={settingsPath} role="menuitem" className="sx-mi" onClick={close}>
                  <FiSettings size={16} aria-hidden="true" /> Settings
                </Link>
              ) : null}
              <button type="button" role="menuitem" className="sx-mi bad" onClick={() => setConfirmOut(true)}>
                <FiLogOut size={16} aria-hidden="true" /> Log out
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One header for staff, members and investors: where you are, a way to jump anywhere, the branch you
 * work in (staff), notifications, and your account.
 */
export default function AppHeader({
  guard = "staff",
  items = [],
  user,
  onMenuClick,
  notificationsPath,
  notificationsEnabled = true,
  profilePath,
}) {
  const { pathname } = useLocation();
  const isStaff = guard === "staff";
  const narrow = useMediaQuery("(max-width: 767px)");

  const here = useMemo(() => locatePage(items, pathname), [items, pathname]);
  const settingsPath = useMemo(() => {
    if (!isStaff) return null;
    const hit = flattenMenu(items).find((e) => e.path.startsWith("/super_admin/settings"));
    return hit ? hit.path : null;
  }, [items, isStaff]);

  const { data: profile } = useGetCurrentStaffProfileQuery(undefined, { skip: !isStaff });
  const current = (profile || user)?.current_branch;
  const branches = asBranches((profile || user)?.branch);

  return (
    <header className="sx sx-header">
      <div className="sx-header-in">
        <button type="button" className="sx-iconbtn sx-burger" onClick={onMenuClick} aria-label="Open menu" aria-controls="sidebar-main">
          <FiMenu size={20} aria-hidden="true" />
        </button>

        <div className="sx-where">
          {here?.crumb ? <div className="sx-crumb">{here.crumb}</div> : null}
          {here?.title ? <div className="sx-title">{here.title}</div> : null}
        </div>

        <div className="sx-actions">
          {items.length ? (
            <button type="button" className="sx-hsearch" onClick={openMenuSearch} aria-label="Search the menu">
              <FiSearch size={18} aria-hidden="true" />
              <span className="lbl">Search pages</span>
              <kbd>Ctrl K</kbd>
            </button>
          ) : null}
          {isStaff && current?.name ? <BranchSwitcher branches={branches} current={current} /> : null}
          <span className="sx-bellwrap">
            <NotificationDropdown guard={guard} enabled={notificationsEnabled} listPath={notificationsPath} mobile={narrow} />
          </span>
          <UserMenu user={user} guard={guard} profilePath={profilePath || (isStaff ? "/super_admin/profile" : "/member/profile")} settingsPath={settingsPath} />
        </div>
      </div>
    </header>
  );
}
